/* eslint max-lines: off */
import { CountryCode } from '@payer-admin-ui/api/src/invoices/models/CountryCode';
import { LanguageCode } from '@payer-admin-ui/api/src/invoices/models/LanguageCode';
import { DeliveryType } from '@payer-admin-ui/api/src/invoices/models/DeliveryType';
import { DeliveryReason } from '@payer-admin-ui/api/src/invoices/models/DeliveryReason';
import { CreditUsageType } from '@payer-admin-ui/api/src/invoices/history/models/CreditUsageType';
import { InternalHistoryEntryType } from '@payer-admin-ui/api/src/invoices/history/models/InternalHistoryEntryType';
import { InvoiceAggregationStatus } from '@payer-admin-ui/api/src/invoices/models/InvoiceAggregationStatus';
import { CustomerType } from '@payer-admin-ui/api/src/invoices/models/CustomerType';
import { PaymentStatus } from '@payer-admin-ui/api/src/invoices/models/PaymentStatus';
import { ThemeValidationErrorCode } from '../../utils/signupTheme/themeValidationErrorCode';

export const en = {
  translation: {
    common: {
      yes: 'Yes',
    },
    actions: {
      title: 'Actions',
      preview: 'Preview PDF',
      sendCopy: 'Send copy via ',
      sendReminder: 'Send reminder',
      sendReminderVia: 'Send reminder via ',
      stopReminders: 'Stop automatic reminders',
      refundCashInvoice: 'Refund cash invoice',
    },
    type: 'Type',
    address: {
      address: 'Address',
      companyName: 'Company name',
      yourReference: 'Your reference',
      title: 'Title',
      fullName: 'Name',
      firstName: 'First name',
      lastName: 'Last name',
      streetAddress1: 'Street address 1',
      streetAddress2: 'Street address 2',
      zipCode: 'Zip code',
      city: 'City',
      phoneNumber: 'Phone',
      emailAddress: 'Email address',
      gln: 'GLN (Global Location Number)',
      state: 'State',
      coAddress: 'C/O address',
      houseAddress: 'House address',
    },
    amounts: {
      amount: 'Amount',
      remainingUnmappedAmount: 'Remaining amount to map',
      balance: 'Balance',
      feeBalance: 'Fee Balance',
      originalAmount: 'Original amount',
      creditedAmount: 'Credited amount',
    },
    bankAccount: {
      account: 'Bank account',
      name: 'Name',
      number: 'Account number',
      holder: 'Account holder',
      bic: 'BIC',
      viaAccountNumber: 'Via account number',
      viaAccountNumberType: 'Via account number type',
    },
    components: {
      breadcrumbs: {
        Home: 'Home',
      },
      blockNavigationProvider: {
        header: 'The following problems blocked navigation',
      },
      buttons: {
        ok: 'Ok',
        add: 'Add',
        apply: 'Apply',
        cancel: 'Cancel',
        edit: 'Edit',
        save: 'Save',
        close: 'Close',
        clear: 'Clear',
        confirm: 'Confirm',
        continue: 'Continue',
        CopyToClipboardIconButton: {
          justCopiedTooltip: 'Copied!',
        },
        create: 'Create',
        createAndSend: 'Create and send',
        submit: 'Submit',
        remove: 'Remove',
        retry: 'Retry',
        pinSidebar: 'Pin sidebar',
        unpinSidebar: 'Unpin sidebar',
        duplicate: 'Duplicate',
        delete: 'Delete',
        confirmClear: 'Are you sure you want to clear the value?',
      },
      ai: {
        chat: {
          pane: {
            title: 'AI Assistant',
            actions: {
              pin: 'Pin',
              unpin: 'Unpin',
              close: 'Close',
              more: 'More',
            },
            menu: {
              copyConversation: 'Copy current conversation',
            },
            messageActions: {
              copy: 'Copy',
              copied: 'Copied',
            },
            emptyState: {
              prompt: "Ask questions about invoices, payments, the platform or anything else that's on your mind!",
            },
            agreementGate: {
              messagePrefix: 'To start using the AI assistant, first ',
              messageLinkText: 'read through and accept our Terms & Conditions',
            },
            termsModal: {
              title: 'Terms & Conditions',
              accept: 'accept',
              content:
                '## Purpose\n' +
                'The AI assistant is provided to help you navigate and use Payer’s services within this portal. Use outside of Payer-related topics is discouraged.\n' +
                '\n' +
                '## Data processing\n' +
                'Messages and conversation history are processed in the Nordics, in the same location(s) where we process and store the rest of our customer data.\n' +
                '\n' +
                '## Accuracy and decision-making\n' +
                'The assistant may return incorrect, incomplete, or misleading information. You are responsible for verifying outputs and for any actions you take in the portal, including actions taken based on recommendations.\n' +
                '\n' +
                '## Liability\n' +
                'To the maximum extent permitted by applicable law, Payer provides the assistant “as is” and disclaims all warranties. Payer is not liable for losses or damages arising from use of the assistant or reliance on its output.\n' +
                '\n' +
                '## Conversation storage and abuse prevention\n' +
                'Conversations are stored to let you revisit them and to help prevent abuse. Payer may use conversations in anonymized and aggregated form to improve the service and understand common customer needs.\n' +
                '\n' +
                '## Responsible use\n' +
                'Do not share secrets, credentials, or personal data unless it is necessary for your intended use and permitted by your organisation’s policies. Do not attempt to use the assistant to bypass access controls, policies, or legal obligations.\n' +
                '\n' +
                '## Service limitations\n' +
                'The service may be limited, throttled, or unavailable at times. Limits may apply to request volume and conversation length.\n' +
                '\n' +
                '## Changes\n' +
                'We may update these terms and the assistant over time. You may be asked to accept updated terms to continue using the service.',
            },
            tabs: {
              conversations: 'Conversations',
              newConversation: 'New conversation',
              newChat: 'New chat',
            },
            composer: {
              status: {
                noResponse: 'There was an issue with generating the response. Try again.',
                quotaExceeded:
                  'You have exceeded your AI usage quota. Talk to your administrator if you wish to increase the quota, otherwise it will reset at {{resetAt}}',
                conversationLengthExceeded:
                  'This conversation is too long to continue. Start a new conversation to keep chatting.',
                error: 'There was an issue with generating the response. Try again.',
              },
              modelMode: {
                fast: 'Fast',
                smart: 'Smart',
              },
            },
            recommendations: {
              invoiceDebtCollection: 'Start debt collection for invoice {{runningNumber}}',
              sendInvoiceReminder: 'Send invoice reminder for invoice {{runningNumber}}',
              sendInvoiceCopy: 'Send invoice copy for invoice {{runningNumber}}',
              cancelInvoiceReminders: 'Stop automatic reminders for invoice {{runningNumber}}',
              stopInvoiceDebtCollection: 'Stop automatic debt collection for invoice {{runningNumber}}',
              downloadInvoicePdf: 'Download invoice PDF for invoice {{runningNumber}}',
              bankFilePaymentAddMapping: 'Add mapping for bank file payment {{id}}',
              suggestedBankfileMappings: 'View suggested mappings',
            },
            draftInvoiceCreationRecommendation: {
              title: 'Draft invoice created',
              summary: {
                invoiceNumber: 'Invoice number',
                customer: 'Customer',
                deliveryRecipient: 'Delivery recipient',
                orderReference: 'Order reference',
                referenceNumber: 'Reference number',
                invoiceDate: 'Invoice date',
                dueDate: 'Due date',
                amountToPay: 'Amount to pay',
                currency: 'Currency',
                deliveryType: 'Delivery type',
                items: 'Items',
              },
              finalise: {
                confirmTitle: 'Are you sure you want to finalize invoice {{invoiceNumber}}?',
                sendInvoiceAutomatically: 'Send invoice automatically',
                confirmButton: 'Yes, finalise invoice',
              },
            },
          },
          toolCalls: {
            unknown: 'Assistant executed an action',
            resultCount_one: 'found {{count}} result',
            resultCount_other: 'found {{count}} results',
            singleEntityFetch: {
              success: 'Retrieved {{entity}} with {{idLabel}} {{id}}',
              failure: 'Failed to retrieve {{entity}} with {{idLabel}} {{id}}',
            },
            entities: {
              invoiceSummary: 'invoice summary',
              invoiceDetails: 'invoice details',
              bankfilePayment: 'bankfile payment',
            },
            argLabels: {
              invoiceNumber: 'invoice number',
              for: 'for',
              amountRange: 'amount range',
              bankfileId: 'bankfile id',
              limit: 'limit',
              id: 'id',
            },
            tools: {
              createDraftInvoiceV3: 'Created draft invoice',
              getInvoiceSummary: 'Retrieved invoice summary',
              getVerboseInvoiceDetailsAvoidUnlessNoOtherOption: 'Retrieved full invoice details',
              getInvoice: 'Retrieved invoice details',
              getItemsForInvoice: 'Retrieved invoice items',
              searchInvoicesFreeText: 'Searched invoices',
              listOverdueUnpaidInvoicesOneYearBack: 'Listed overdue unpaid invoices',
              searchMappingInvoiceCandidates: 'Searched invoices for mapping candidates',
              searchPaymentsFreeText: 'Searched payments',
              searchBankFilePaymentsFreeText: 'Searched bankfile payments',
              listUnmappedBankFilePayments: 'Listed unmapped bankfile payments',
              getBankFilePaymentById: 'Retrieved bankfile payment',
              searchInvoiceStatementsFreeText: 'Searched invoice statements',
            },
          },
          button: {
            tooltip: 'AI Chat',
          },
        },
      },
      editors: {
        standardTextEditor: {
          tipSaveWithCtrlS: 'Tip: save your changes with Ctrl / ⌘ + S',
        },
      },
      cards: {
        subscription: {
          title: 'Subscription {{externalReferenceId}}',
          details: {
            title: 'Subscription details',
            noSubscriptionPaymentMethod: 'Select payment method',
            nextBillingDate: 'Next billing date',
            dueDays: 'Due days',
            periodTime: 'Period time',
            externalReferenceId: 'External reference ID',
            subscriptionPaymentMethod: 'Subscription payment method',
            currencyCode: 'Currency',
            noCurrencyCode: 'Select currency',
            orderReferenceId: 'Order number/order reference',
            type: 'Invoice type',
            deliveryType: 'Delivery type',
            yourReference: 'Invoice reference',
            additionalReferenceId: 'Additional reference',
            description: 'Internal order description',
            comment: 'Order comment',
            paymentMethods: 'Payment methods',
          },
        },
        invoice: {
          title: 'Invoice {{invoiceNumber}}',
          loadingTitle: 'Invoice',
          items: 'Items',
          editItems: {
            description:
              'Items will appear on the invoice with the order you give them here. You can reorder items using the buttons or drag and drop.',
          },
          summary: {
            sumSymbol: 'Σ',
            vat: 'VAT',
            vatPercentage: 'VAT (%)',
            vatBasis: 'VAT basis',
            vatAmount: 'VAT amount',
            totalVatAmount: 'Total VAT amount',
            totalExcludingVat: 'Total excl. VAT',
            totalIncludingVat: 'Total incl. VAT',
            amountToPay: 'Amount to pay',
          },
          invoiceDetails: {
            title: 'Details',
            invoiceDate: 'Invoice date',
            dueDate: 'Due date',
            invoicePeriod: {
              label: 'Invoice period',
              startDate: 'Period start date',
              endDate: 'Period end date',
              info: 'Optional. Specify a period if it differs from the invoice date (for example, services delivered earlier) or if your customer requires it. This supports accurate and automated accounting.',
            },
            currencyCode: 'Currency',
            noCurrencyCode: 'Select currency',
            orderReferenceId: 'Order number/order reference',
            type: 'Invoice type',
            deliveryType: 'Delivery type',
            yourReference: 'Invoice reference',
            site: 'Site',
            additionalReferenceId: 'Additional reference',
            description: 'Internal order description',
            comment: 'Order comment',
            yourOrderNumber: 'Buyer order number/P.O. number',
            paymentMethods: 'Payment methods',
            placeholders: {
              currency: 'Enter currency',
              invoiceType: 'Enter invoice type',
              deliveryType: 'Enter delivery type',
              orderReferenceId: 'Enter order reference id',
              orderNumber: 'Enter order number',
              additionalReferenceId: 'Enter additional reference id',
              yourReference: 'Enter your reference',
              site: 'Enter site',
              description: 'Enter description',
              comment: 'Enter comment',
            },
            invoiceStatement: 'Invoice statement',
            includeInInvoiceStatement: 'Include in invoice statement for this month',
            externalInvoiceNumber: 'External invoice number',
            externalReference: 'External reference',
            reference: 'Reference',
            creditsInvoiceWithInvoiceNumber: 'Credits invoice',
            standalone: 'Standalone',
          },
          customer: {
            header: 'Customer data',
            invoiceAddress: 'Invoice address',
            getAddress: 'Autofill address',
            getAddressTooltipTemp: 'Autofill address is currently unavailable',
            getAddressTooltip: 'Only available for Swedish customers. Requires a valid registration number.',
            type: 'Type',
            country: 'Country',
            language: 'Language',
            placeholders: {
              customerNumber: 'Enter customer number',
              regNumber: 'Enter registration number',
              vatId: 'Enter VAT id',
              address: {
                companyName: 'Enter company name',
                yourReference: 'Enter your reference',
                firstName: 'Enter first name',
                lastName: 'Enter last name',
                streetAddress1: 'Enter street address',
                streetAddress2: 'Enter additional street address data',
                zipCode: 'Enter postal code',
                city: 'Enter city',
                emailAddress: 'Enter email address',
                phoneNumber: 'Enter phone number',
                gln: 'Enter gln',
              },
            },
          },
        },
        invoiceAggregation: {
          title: 'Monthly statement {{invoiceAggregationNumber}}',
          periodStart: 'Period start',
          periodEnd: 'Period end',
        },
        creditUsages: {
          title: 'Credit usage',
          creditUsageTypes: {
            [CreditUsageType.MANUAL]: 'Manual',
            [CreditUsageType.PAID_OUT]: 'Payout',
            [CreditUsageType.USED_AS_PAYMENT]: 'Used as payment',
          },
          headers: {
            col1: 'Credit usage ID',
            col2: 'Mapping time',
            col3: 'Credit used',
            col4: 'Credit type',
            col5: 'Invoice number',
          },
          summary: {
            headers: {
              col1: 'Original credit',
              col2: 'Unused credit',
              col3: 'Matched with crediting invoice',
              col4: 'Link',
            },
          },
        },
        distribution: {
          title: 'Distribution',
          'no info': 'no info',
          'no data': 'no data',
          'no reference': 'no reference',
          reasons: {
            [DeliveryReason.INITIAL]: 'Invoice',
            [DeliveryReason.REMINDER]: 'Reminder',
            [DeliveryReason.COPY]: 'copy',
          },
          types: {
            [DeliveryType.EMAIL]: 'Email',
            [DeliveryType.SMS]: 'Sms',
            [DeliveryType.PRINT]: 'Postal',
            [DeliveryType.EINVOICE]: 'E-Invoice',
          },
          headers: {
            col1: 'Dist item',
            col2: 'Dist type',
            col3: 'Send time',
            col4: 'Successful distribution',
            col5: 'Receiver',
          },
        },
        invoiceHistory: {
          title: 'Invoice history',
          'no info': 'no info',
          att: 'Att.',
          stepTexts: {
            [InternalHistoryEntryType.INVOICE_CREATED]: {
              left: {
                invoiceDate: 'created at',
                dueDate: 'due date',
              },
              right: {
                amount: 'Amount (incl. VAT)',
                link: 'Link',
              },
            },
            [InternalHistoryEntryType.INVOICE_ATTACHMENT]: {
              left: {
                uploadedAt: 'Uploaded at',
                filename: 'Filename',
                fileSize: 'File size',
              },
              right: {
                link: 'Link',
                downloadAttachment: 'Download attachment',
              },
            },
            [InternalHistoryEntryType.INVOICE_FINALISED]: {
              left: {
                finalisedAt: 'Finalised at',
              },
            },
            [InternalHistoryEntryType.INVOICE_DISTRIBUTED]: {
              right: 'Recipient(s)',
            },
            [InternalHistoryEntryType]: {
              left: 'Cancelled',
            },
            [InternalHistoryEntryType.CREDIT_USED]: {
              left: 'Credit used',
              right: 'Used amount',
              paidInvoice: 'Paid invoice',
            },
            [InternalHistoryEntryType.CREDIT_MATCHED]: {
              left: 'Credit used',
              right: 'Used amount',
              creditInvoice: 'Credit invoice',
            },
            [InternalHistoryEntryType.INVOICE_PAYMENT_REFUNDED]: {
              left: 'Refunded',
              right: 'Refunded amount',
            },
            [InternalHistoryEntryType.INVOICE_CREDITED]: {
              left: {
                full: 'Fully credited',
                partial: 'Partially credited',
              },
              right: 'Credited amount',
            },
            [InternalHistoryEntryType.INVOICE_PAYMENT]: {
              left: {
                full: 'Fully paid',
                partial: 'Partially paid',
              },
              right: 'Amount paid',
            },
            [InternalHistoryEntryType.INVOICE_CANCELLED]: {
              left: 'Cancelled',
            },
          },
          subheaders: {
            [InternalHistoryEntryType.INVOICE_CREATED]: 'Invoice created',
            [InternalHistoryEntryType.INVOICE_ATTACHMENT]: 'Invoice attachment',
            [InternalHistoryEntryType.INVOICE_FINALISED]: 'Invoice finalised',
            [InternalHistoryEntryType.INVOICE_UPDATED]: 'Invoice updated',
            [InternalHistoryEntryType.INVOICE_CANCELLED]: 'Invoice cancelled',
            [InternalHistoryEntryType.INVOICE_CREDITED]: 'Invoice credited',
            [InternalHistoryEntryType.INVOICE_PAYMENT]: 'Invoice payment',
            [InternalHistoryEntryType.INVOICE_PAYMENT_REFUNDED]: 'Invoice payment refunded',
            [InternalHistoryEntryType.INVOICE_REMINDER]: 'Invoice reminder created',
            [InternalHistoryEntryType.INVOICE_REMINDER_FEE_CANCELLED]: 'Invoice reminder fee cancelled',
            [InternalHistoryEntryType.INVOICE_DEBT_COLLECTION]: 'Invoice sent to debt collection',
            [InternalHistoryEntryType.CREDIT_USED]: 'Credit used',
            [InternalHistoryEntryType.CREDIT_MATCHED]: 'Invoice payment with credit',
            [InternalHistoryEntryType.INVOICE_DISTRIBUTED]: {
              regular: {
                [DeliveryType.EMAIL]: 'PDF invoice sent by email',
                [DeliveryType.SMS]: 'PDF invoice sent by phone',
                [DeliveryType.PRINT]: 'Sent physical invoice by mail',
                [DeliveryType.EINVOICE]: 'Sent as E-invoice',
              },
              reminder: {
                [DeliveryType.EMAIL]: 'PDF invoice reminder sent by email',
                [DeliveryType.SMS]: 'PDF invoice reminder sent by phone',
                [DeliveryType.PRINT]: 'Sent physical invoice reminder by mail',
                [DeliveryType.EINVOICE]: 'Sent reminder as E-invoice',
              },
            },
          },
        },
        about: {
          about: 'About',
          registrationNumber: 'Registration number',
          country: 'Country code',
        },
        people: {
          people: 'People',
          numberOfPeople: 'Registered people',
        },
        credit: {
          credit: 'Credit',
          status: 'Status',
          limit: 'Limit',
          date: 'Date',
          decision: 'Decision',
        },
        actionSummary: {
          deliveryAddress: 'Delivery address',
          invoiceAddress: 'Invoice address',
          country: 'Country',
          orderer: 'Orderer',
          emailAddress: 'Email address',
          phoneNumber: 'Phone number',
          registrationNumber: 'Registration number',
          registrationDate: 'Registration date',
          status: 'Status',
        },
        identificationVerification: {
          identificationType: 'Identification type',
        },
        approverVerification: {
          approverName: 'Approver name',
          boardMembers: 'Company board members',
          authSigns: 'Company authorized signatories',
        },
        defaultSessionAction: {
          createdDate: 'Initialized date',
        },
      },
      dropdown: {
        noItemsConfigured: 'No items configured',
        none: 'None',
        selectItem: 'Select item',
      },
      header: {
        account: 'Account',
        logout: 'Logout',
      },
      searchbar: {
        search: 'Search',
        clearSearch: 'Clear search',
        searchOn: 'Search on',
        searchInvoices: 'Search invoices',
        invoiceSearchResults: 'Invoice search results',
        noResults: 'No results',
      },
      table: {
        property: 'Property',
        value: 'Value',
        noRows: 'No rows',
        noResults: 'No results',
        filterOverride: {
          notice: 'You are viewing data from a dashboard filter. Exit the view to return to your normal filters.',
          exit: 'Exit view',
        },
        clearCustomizedTableSettings: {
          label: 'Clear customized table settings',
          tooltip:
            'Clears all customized table settings, including column visibility, column widths, filters, sorting, and pagination',
        },
      },
    },
    date: {
      startDate: 'Start date',
      endDate: 'End date',
    },
    dialog: {
      sessionExpiredWarning: {
        title: 'Your session is ending!',
        content: 'Your current session will end in 10 minutes and you will be automatically logged out.',
        actions: {
          confirm: 'Log out',
          refresh: 'Stay Logged In',
        },
      },
    },
    error: {
      error: 'Error',
      missingErrorCode: 'Unspecified error',
      serverResponseJsonSyntax: 'Bad JSON from server',
      serverResponseNotJson: 'Unknown server error',
      serverUnavailable: 'Server unavailable',
      invoiceOrInvoiceStatementNotFound:
        'No invoice or invoice statement found, please control input information or try another search',
      subscriptionPaymentMethod: 'Subscription payment method must be either invoice or card.',
      unauthorized: 'Your session has expired or you lack necessary permissions. Try logging in again.',
      bankid: {
        failed: 'Sign in with BankID failed',
        consumed: 'BankID session already consumed',
        errorNoClients: 'There are no clients associated with your BankID at Payer',
      },
      format: {
        errorCount: 'Error count',
        latestError: 'Latest error',
        doNotRetryUntil: 'Do not retry until',
        noErrorCount: 'No error count found',
        noDate: 'No date found',
      },
      invoice: {
        cantMapCreditNoteToIa: "Can't map a credit note to monthly statement(s)",
        iaAndInvoiceMixed: "Can't map to both monthly statement(s) and invoice(s) at the same time",
        create: {
          validationErrors: {
            dueDaysNotPartOfInvoiceStatement: 'Due days is required when invoice is not part of invoice statement',
            draftEinvoiceSend: 'Not possible to send draft invoices as Einvoice',
            regNumberOrVatId: 'Either registration number or VAT id is required',
            invoicePeriod: 'Both start and end dates must be specified for invoice period',
            orderReference: 'Order reference is required',
            streetAddress1: 'Street address is required',
            zipCode: 'Zip code is required',
            city: 'City is required',
            description: 'Description is required',
            descriptionMaxLength: 'Description cannot exceed 128 characters',
            quantity: 'Quantity must be greater than 0.000001',
            vatPercentage: 'VAT percentage must be either 0 or between 1 and 100',
            items: 'At least one item is required',
            emailRequired: 'Email address is required when delivery type is Email',
            articleNumberMaxLength: 'Article number cannot exceed 64 characters',
            unitMaxLength: 'Unit cannot exceed 16 characters',
            additionalDetailMaxLength: 'Additional detail cannot exceed 48 characters',
            noteMaxLength: 'Note cannot exceed 48 characters',
            costCenterMaxLength: 'Cost center cannot exceed 48 characters',
            articleNumberRequired: 'Article number is required for FREEFORM items',
            externalInvoiceNumberMaxLength: 'External invoice number cannot exceed 36 characters',
            orderReferenceMaxLength: 'Order reference cannot exceed 48 characters',
            additionalReferenceIdMaxLength: 'Additional reference ID cannot exceed 48 characters',
            commentMaxLength: 'Comment cannot exceed 128 characters',
            ourReferenceMaxLength: 'Our reference cannot exceed 48 characters',
            yourReferenceMaxLength: 'Invoice reference cannot exceed 48 characters',
            contractReferenceMaxLength: 'Contract reference cannot exceed 48 characters',
            yourOrderNumberMaxLength: 'Your order number cannot exceed 48 characters',
            siteMaxLength: 'Site cannot exceed 48 characters',
            totalAmountExcludingVatMin: 'Total amount excluding VAT must be 0 or greater',
            totalAmountIncludingVatMin: 'Total amount including VAT must be 0 or greater',
            regNumberMaxLength: 'Registration number cannot exceed 48 characters',
            customerVatIdMaxLength: 'Customer VAT ID cannot exceed 48 characters',
            externalCustomerNumberMaxLength: 'External customer number cannot exceed 48 characters',
            companyNameMaxLength: 'Company name cannot exceed 128 characters',
            addressYourReferenceMaxLength: 'Your reference cannot exceed 48 characters',
            titleMaxLength: 'Title cannot exceed 24 characters',
            firstNameMaxLength: 'First name cannot exceed 48 characters',
            lastNameMaxLength: 'Last name cannot exceed 48 characters',
            streetAddress1MaxLength: 'Street address 1 cannot exceed 128 characters',
            streetAddress2MaxLength: 'Street address 2 cannot exceed 128 characters',
            coAddressMaxLength: 'C/O address cannot exceed 128 characters',
            houseAddressMaxLength: 'House address cannot exceed 128 characters',
            zipCodeMaxLength: 'Zip code cannot exceed 20 characters',
            cityMaxLength: 'City cannot exceed 48 characters',
            stateMaxLength: 'State cannot exceed 64 characters',
            emailAddressMaxLength: 'Email address cannot exceed 128 characters',
            phoneNumberMaxLength: 'Phone number cannot exceed 128 characters',
            emailAddressCcMaxLength: 'CC email address cannot exceed 128 characters',
            emailAddressBccMaxLength: 'BCC email address cannot exceed 128 characters',
            glnMaxLength: 'GLN cannot exceed 128 characters',
            regNumberRequired: 'Registration number is required when delivery type is Einvoice',
            companyNameRequiredEinvoice: 'Company name is required when delivery type is Einvoice',
            totalLineItemsPrice: 'The total line items price must be greater than zero',
            yourReferenceRequired: 'Your reference is required when customer type is Company',
            companyNameRequiredCustomerType: 'Company name is required when customer type is Company',
            firstNameRequiredCustomerType: 'First name is required when customer type is Consumer',
            lastNameRequiredCustomerType: 'Last name is required when customer type is Consumer',
            nameRequired: 'First name and last name must be set if company name is not set',
            invoiceDate: 'Not allowed to specify a invoice date after today',
            invalidDate: 'Invalid date',
            externalPaymentReferenceOCRNumber: 'External payment reference must be a number when type is OCR',
            salesAccountCode: 'Sales account code must be between 1000 and 9999',
            deliveryTypeNotNone: 'Delivery type must be None when invoice is part of invoice statement',
          },
        },
      },
      modal: {
        title: 'Something went wrong',
      },
      paymentRequest: {
        positiveAmount: 'Amount must be positive',
        externalPaymentReferenceRequired: 'External payment reference is required',
        externalPaymentReferenceMaxLength: 'External payment reference must be at most 40 characters',
      },
      theme: {
        [ThemeValidationErrorCode.INVALID_JSON]: 'Theme is not a valid json',
        [ThemeValidationErrorCode.PROP_NOT_ALLOWED]: 'The property "{{prop}}" is not allowed',
        [ThemeValidationErrorCode.INVALID_PROP_VALUE]: 'The value of property "{{prop}}" is is not a valid {{type}}',
        [ThemeValidationErrorCode.NOT_SUPPORTED_PROP_TYPE]: 'The provided property type of "{{prop}}" is not allowed',
      },
      validation: {
        string: 'Must be a string',
        notEmpty: 'Must not be empty',
        lengthTooShort: 'Length {{length}} too short, must be at least {{minLength}}',
        lengthTooLong: 'Length {{length}} too long, must be at most {{maxLength}}',
        allowedCharacters_one:
          'Character <code>{{disallowedCharacters}}</code> not allowed according to regex <code>{{allowedCharacters}}</code>',
        allowedCharacters_other:
          'Characters <code>{{disallowedCharacters}}</code> not allowed according to regex <code>{{allowedCharacters}}</code>',
        email: 'Must be a valid email',
        phoneE164: 'Must be a valid phone number in E.164 format, e.g. +46701234567',
        number: 'Must be a number',
        max2FractionDigits: 'Must have at most 2 decimals',
        maxInclusive: 'Must be less than or equal to {{maxValue}}',
        minInclusive: 'Must be greater than or equal to {{minValue}}',
        positive: 'Must be positive',
        negative: 'Must be negative',
        notZero: 'Must not be zero',
        integer: 'Must be an integer',
        percentage: 'Must be between 0 and 100',
        currency: "Currency doesn't match",
        minDate: 'Selected date too early',
        maxDate: 'Selected date too late',
        invalidDate: 'Selected date is invalid',
        modalMessage: 'There were errors in the input data, please correct the highlighted fields.',
      },
    },
    fields: {
      webView: 'Web view',
    },
    filter: {
      clearFilters: 'Clear filters',
      clearAll: 'Clear All',
      clear: 'Clear',
      applyFilters: 'Apply Filters',
      filters: 'Filters',
      misc: 'Misc',
      amount: 'Amount Range',
      dateRange: {
        from: 'From',
        to: 'To',
        shortcuts: {
          today: 'Today',
          thisWeek: 'This Week',
          lastWeek: 'Last Week',
          thisMonth: 'This Month',
          lastMonth: 'Last Month',
          thisYear: 'This Year',
          lastYear: 'Last Year',
        },
      },
      placeholders: {
        'Select country': 'Select country',
        'Select currency': 'Select currency',
        'Select payment status': 'Select payment status',
      },
      titles: {
        'Delivery time start': 'Delivery time start',
        'Delivery time end': 'Delivery time end',
      },
      buttonText: 'Filters',
      tooltip: 'Filters',
    },
    login: {
      merchantPortal: 'Merchant portal',
      mobileBankid: 'Mobile BankID',
      signIn: 'Sign in',
      clientCredentials: 'Client credentials',
      loginBankId: 'Start BankID login',
      bankIdQrInstructions:
        'Open the BankID app on your phone and tap the QR code icon on the app to scan the QR code below.',
      selectMerchant: 'Select a merchant',
    },
    notifications: {
      title: 'Notifications',
      configureSubscriptions: 'Configure notification subscriptions',
      configureSubscriptionsHint: 'Choose what you get notified about',
      notificationSubscriptions: 'Notification subscriptions',
      configureSubscriptionsDescription:
        'Subscribe to notifications for the events that are important to you. Choose whether you want to be notified via email, or right here in the merchant portal.',
      eventDescriptions: {
        INVOICE_NEARING_DEBT_COLLECTION:
          'Notifies you a few days before an invoice is automatically sent for debt collection',
        CARD_PAYMENT_ERROR: 'Notifies you when a card payment was declined or had an error',
        CARD_PAYMENT_SETTLED: 'Notifies you when a card payment is fully or partially captured',
      },
      notSubscribing: 'Not currently subscribing to event',
      notifyMeOfThisEvent: 'Notify me of this event',
      missingRequiredRoleForSubscription: 'You lack necessary permissions to subscribe to this event',
      eventType: 'Event type',
      channels: 'Notify me via',
      scopeType: 'Notify me for',
      site: 'Site',
      sites: 'Sites',
      noSitesConfiguredTooltip:
        'You have no explicit sites configured. Contact Payer for assistance with the necessary adjustments.',
      emailDisabledNoAddress: 'Configure an email address in the account settings to get email notifications',
      emailDisabledForEvent:
        'E-mail notifications for {{eventTitle}} are not available at the moment. Reach out if you have a need for them!',
      allInvoices: 'All invoices',
      customersHavePendingSessions_one: 'Customers have 1 pending session',
      customersHavePendingSessions_other: 'Customers have {{count}} pending sessions',
      customersHaveFailedSessions_one: 'Customers have 1 recent failed session',
      customersHaveFailedSessions_other: 'Customers have {{count}} recent failed sessions',
      customersHaveManualControlSessions_one: 'Customers have 1 session that requires manual control',
      customersHaveManualControlSessions_other: 'Customers have {{count}} sessions that require manual control',
      customersHaveErrorSessions_one: 'Customers have 1 error session',
      customersHaveErrorSessions_other: 'Customers have {{count}} error sessions',
      details: {
        settled: 'Settled',
        error: 'Error',
        invoiceNumbers: 'Invoice numbers',
      },
    },
    userSettings: {
      title: 'User settings',
      emailInstruction:
        'If you want to receive notifications via email, you can add or update your email address here.',
      invalidEmail: 'Invalid email address',
      emailUpdateFailed: "We couldn't update your email address. Please try again later.",
      emailUpdatedSuccessfully: 'Email address updated!',
    },
    pagination: {
      results: 'results',
      show: 'Show',
      rowsPerPage: 'Rows per page:',
      of: 'of',
    },
    reports: {
      includeHeadersLabel: 'Include CSV headers',
      submit: 'Get CSV',
      fromDate: 'From date',
      toDate: 'To date',
      accountReceivables: 'Account receivables report',
      bankPayments: 'Bank payments report',
    },
    root: {
      loading: 'Loading...',
      sessionIsFetching: "Checking if you're logged in...",
      missingRequiredRoleForPage: 'You lack necessary permission to access this page',
    },

    internalUi: {
      agentsAndContext: {
        title: 'Agents & Context',
        tabs: {
          context: 'Context',
          agents: 'Agents',
        },
        agents: {
          empty: 'Agents will live here.',
        },
        context: {
          errors: {
            fetchDocument: 'Failed to fetch document details',
            fetchRevision: 'Failed to fetch revision',
            deleteRevision: 'Failed to delete revision',
            createRevision: 'Failed to create revision',
            activateRevision: 'Failed to activate revision',
            rollback: 'Failed to rollback to default',
            previewSystemPrompt: 'Failed to preview system prompt',
          },
          documents: {
            title: 'Documents',
            groups: {
              system: 'System',
              invoice: 'Guide · Invoice',
              bankFilePayment: 'Guide · Bank file payments',
            },
            known: {
              system: {
                domain: 'Domain',
                merchantPortal: 'Merchant portal',
                internalAdmin: 'Internal admin',
                chatEndpoint: 'Chat endpoint',
              },
              invoice: {
                base: 'Base',
                statusAppendix: 'Status appendix',
                ocrAppendix: 'OCR appendix',
                balanceAppendix: 'Balance appendix',
              },
              bankFilePayment: {
                base: 'Base',
                mappingAppendix: 'Mapping appendix',
                fundSourceTypeAppendix: 'Fund source type appendix',
                mappingTypeAppendix: 'Mapping type appendix',
                mappingErrorsAppendix: 'Mapping errors appendix',
              },
            },
          },
          document: {
            selectDocument: 'Select a document',
            loading: 'Loading document…',
            actions: {
              viewActive: 'View active revision',
              activate: 'Activate',
              active: 'Active',
              activating: 'Activating…',
              rollbackToDefault: 'Rollback to default',
              rollingBack: 'Rolling back…',
              createNewRevision: 'Create new revision',
              deleteRevision: 'Delete revision',
              deletingRevision: 'Deleting…',
            },
            delete: {
              title: 'Delete revision',
              body: 'Delete revision {{revisionId}}?',
              confirm: 'Delete',
              cancel: 'Cancel',
            },
            revisions: {
              title: 'Revisions',
              empty: 'No revisions found.',
              active: '(active)',
            },
            selector: {
              label: 'Select version',
              default: 'Default',
              noRevisionDetails: 'No details available',
            },
            content: {
              label: 'Content',
              loading: 'Loading revision…',
            },
            revisionDetails: {
              title: 'Revision details',
              empty: 'Select a revision to view details.',
              revisionId: 'Revision id',
              created: 'Created',
              noCreatedInfo: 'No creation info',
              changeNote: 'Change note',
              noChangeNote: 'No change note',
            },
            editor: {
              title: 'New revision',
              changeNoteLabel: 'Change note',
              contentLabel: 'Content',
              creating: 'Creating…',
              save: 'Save',
              saving: 'Saving…',
            },
          },
          systemPromptPreview: {
            openButton: 'Preview system prompt',
            title: 'System prompt preview',
            internalAdmin: 'internalAdmin',
            refresh: 'Refresh',
            loading: 'Loading preview…',
          },
        },
      },
    },

    bankFilePayments: {
      fields: {
        creationTime: 'Creation Time',
        credit: 'Credit',
        creditInvoiceReference: 'Credit ref',
        customer: 'Customer',
        date: 'Date',
        debtor: 'Debtor',
        ultimateDebtor: 'Ultimate debtor',
        emailSent: 'Email sent',
        endCustomerEmailSent: 'Email Sent to Customer',
        id: 'ID',
        mappedToCreditInvoiceNumber: 'Mapped to creditInvoiceNumber',
        mappedBy: 'Mapped by',
        mappedToInvoiceNumber: 'Mapped to invoiceNumber',
        mappingError: 'Mapping error',
        mappings: 'Mappings',
        normal: 'Normal',
        number: 'Number',
        remaining: 'Remaining',
        total: 'Total',
        originReference: 'Origin reference',
        paymentNotificationEmailSent: 'Payment notification email sent',
        status: 'Status',
        statement: 'Statement',
        invoice: 'Invoice',
        bookingDate: 'Booking date',
        valueDate: 'Value date',
        fundSourceType: 'Fund source type',
        debtorName: 'Debtor name',
        debtorRegistrationNumber: 'Debtor registration number',
        debtorAddress: 'Debtor address',
        ticket: 'Ticket',
        time: 'Time',
        toMerchant: 'To merchant',
        toMerchantMatchConfidence: 'Confidence: {{matchConfidence}}',
        toPayer: 'To Payer',
        type: 'Type',
        mapAmount: 'Map amount',
        mappingType: 'Mapping type',
      },
      filters: {
        mappingStatus: 'Mapping Status',
        mappingError: 'Mapping Error',
        accountType: 'Account Type',
        manuallyMapped: 'Manually mapped',
        doNotMap: 'Do Not Map',
        toPayer: 'To Payer',
        doNotMapReason: 'Do Not Map Reasons',
        processingDate: 'Processing Date',
        processingDateFrom: 'Processing Date From',
        processingDateTo: 'Processing Date To',
        bookingDate: 'Booking Date',
        bookingDateFrom: 'Booking Date From',
        bookingDateTo: 'Booking Date To',
        fullyMapped: 'Fully Mapped',
        customerName: 'Customer Name',
        id: 'Payin ID',
        fundSourceTypes: 'Fund Source Types',
        values: {
          fullyMapped: {
            true: 'Fully mapped',
            false: 'Not fully mapped',
          },
          fundSourceTypes: {
            CREDIT_INVOICE: 'Credit invoice',
            PAYMENT_TO_ACCOUNT: 'Payment to account',
          },
          accountTypes: {
            BG: 'Bankgiro',
            PG: 'Plusgiro',
            OTHER: 'Other',
          },
        },
      },
      singleView: {
        title: 'Bank file payment {{bankFilePaymentId}}',
        notFound: 'Bank file payment not found',
        from: 'From',
        to: 'To',
        paymentDetails: 'Additional payment information',
        mappingNotes: 'Mapping notes',
        addYourNotes: 'Add your notes',
        merchantNotes: 'Merchant notes',
        internalNotes: 'Notes from support',
        legacyNotes: 'Legacy Notes',
        save: 'Save',
        deleteConfirmationLabel: 'Are you sure you want to delete the note?',
      },
      listView: {
        title: 'Bank file payments',
        noBankFilePaymentsFound: 'No bank file payments found',
        noBankFilePaymentsFoundV2: 'Found no bank file payments matching your filters',
      },
      customer: {
        name: 'Name',
        address: 'Address',
        zipcode: 'ZIP Code',
        regNumber: 'Organization Number',
      },
      mappingType: {
        name: 'Mapping type',
        values: {
          NORMAL: 'Normal',
          OVERPAYMENT: 'Overpayment',
          FEE: 'Fee',
          INTEREST: 'Interest',
          REFUND: 'Refund',
          MANUAL_REFUND: 'Manual refund',
          IGNORED: 'Ignored',
        },
      },
      mapping: {
        mappingProgress: 'Mapping progress',
        amount: 'Original amount to map',
        remainingUnmappedAmount: 'Remaining amount to map',
        invoiceMappings: 'Invoice mappings',
        mappingStatus: 'Mapping status',
        mappingError: 'Mapping Error',
      },
      mappingError: {
        overpayment: 'Overpayment',
        overuse: 'Overuse',
        invoiceNotFound: 'Invoice not found',
        wrongCurrency: 'Wrong currency',
        wrongInvoiceType: 'Wrong invoice type',
        nonMatchingMerchant: 'Non-matching merchant',
        partOfInvoiceAggregation: 'Part of invoice aggregation',
        other: 'Other',
        deniedOrder: 'Denied order',
      },
      info: {
        doNotMap: 'Payin marked as "Do not map"',
      },
      merchantMappingModal: {
        title: 'Map bank file payment {{bankFilePaymentId}}',
        applyMappings: 'Apply new mappings',
        noNewMappingsYetInvoice: 'Search for and select an invoice to begin mapping.',
        noNewMappingsYetInvoiceWithAi:
          'Search for and select an invoice to begin mapping, or ask the Assistant to suggest mappings!',
        noNewMappingsYetStatement: 'Search for and select a statement to begin mapping.',
        noResults: 'No results',
        searchPlaceholder: 'Search invoices and statements',
        bankFilePayment: 'Bank file payment',
        newMappings: 'New mappings',
        aiSuggestedMapping: 'AI-suggested mapping',
        failedToApplyMappings: 'Failed to apply mappings',
        mappingsApplied: 'Applied {{count}} mappings from bankfile payment {{bankFilePaymentId}} to {{type}}s',
        suggestMappings: 'Suggest mappings',
        suggestMappingsPrompt:
          'Help me map bank file payment {{bankFilePaymentId}}. Suggest the most reasonable mappings you can find, and only ask me for further information if you can not find any reasonable mappings at all.',
        markRestAsManuallyRefunded: 'Mark rest as manually refunded',
        markRestAsIgnored: 'Mark rest as ignored',
        customerMatches: 'Customer matches bank file payment debtor',
        remainingAmountMatches: 'Invoice remaining amount matches bank file payment amount',
        originalAmountMatches: 'Invoice original amount matches bank file payment amount',
        referenceMatches: 'Invoice reference matches bank file payment reference',
        ocrNumberMatches: 'Statement OCR number matches bank file payment reference',
        mapTo: 'Map to',
        mapToInvoices: 'Invoices',
        mapToStatements: 'Statements',
        searchForReference: 'Search for reference',
        searchForDebtorName: 'Search for debtor name',
        zeroMappingAmount: 'Mapping amount cannot be zero or negative',
        totalAmount: 'Total amount to map',
        remainingUnmappedAmount: 'Remaining unmapped amount',
        noMappings: 'There are no mappings to apply',
        invalidMappingAmounts: 'You can not apply mappings with zero or negative amounts',
        mappingAmountExceedsRemainingUnmapped: 'You can not apply mappings that exceed the remaining unmapped amount',
      },
      actions: {
        copyToClipboard: 'Copy data to clipboard',
        editToMerchant: {
          title: 'Tie payin to merchant',
          body: 'Mark bank file payment {{bankFilePaymentId}} as belonging to',
          merchantId: 'Merchant',
          extraBodyAlreadyTied:
            'Note that the bank file payment was already marked as belonging to {{toMerchant.merchantId}} with confidence {{toMerchant.matchConfidence}}. Though since it has not yet been mapped to any invoice that can still be overridden.',
        },
        ignoreRemainingUnmappedAmount: {
          openModal: 'Ignore remaining unmapped amount',
          title: 'Ignore remaining unmapped amount',
          body: 'Do you want to ignore the remaining unmapped amount, {{remainingUnmappedAmountAndCurrency}}, of bank file payment {{bankFilePaymentId}}?',
          extraBodyNotToPayer:
            "Note that the bank file payment was not issued to Payer's account. Are you sure you want to ignore the remaining unmapped amount?",
          submit: 'Ignore',
          success: 'Ignored remaining unmapped amount for bank file payment {{bankFilePaymentId}}',
        },
        markAsDoNotMapIgnored: {
          openModal: 'Ignore the whole payin',
          title: 'Ignore the whole payin',
          body: 'Do you want to completely ignore bank file payment {{bankFilePaymentId}}? I.e. it will never be mapped.',
          submit: 'Ignore payin',
          success: 'Bank file payment {{bankFilePaymentId}} marked as ignored',
        },
        markAsManuallyRefunded: {
          openModal: 'Mark remaining amount as manually refunded',
          title: 'Mark remaining amount as manually refunded',
          body: 'Do you want to mark as manually refunded the remaining unmapped amount, {{remainingUnmappedAmountAndCurrency}}, of bank file payment {{bankFilePaymentId}}?',
          extraBodyNotToPayer:
            "Note that the bank file payment was not issued to Payer's account. Are you sure it was manually refunded?",
          submit: 'Mark as manually refunded',
          success: 'Marked remaining amount as manually refunded for bank file payment {{bankFilePaymentId}}',
        },
        refundIgnoredMapping: {
          openModal: 'Refund ignored mappings',
          title: 'Refund ignored mappings',
          body: 'Do you want to refund all positive amount ignored mappings for bank file payment {{bankFilePaymentId}}?',
          submit: 'Refund',
        },
        refundRemainingUnmappedAmount: {
          openModal: 'Refund remaining unmapped amount',
          title: 'Refund remaining unmapped amount',
          body: 'Do you want to refund remaining unmapped amount, {{remainingUnmappedAmountAndCurrency}}, of bank file payment {{bankFilePaymentId}}?',
          submit: 'Refund',
        },
        splitMap: {
          openModal: 'Add bank file payment mapping',
          title: 'Map bank file payment {{bankFilePaymentId}} to multiple invoices or monthly statements',
          submit: 'Map to selected invoices/monthly statements',
          searchCardTitle: 'Add invoice/monthly statement to map to',
          searchPlaceholder: 'Search for id/number/OCR',
          amountTitle: 'Amount to pay to this invoice / monthly statement',
          amountTitleInvoice: 'Amount to pay to this invoice',
          amountTitleIa: 'Amount to pay to this monthly statement',
          addWantedMapping: 'Add to list of wanted mappings',
          wantedMappingsCardTitle: 'Wanted mappings',
          totalAmountCardTitle: 'Total amount to map',
          pairOffAmountCardTitle: 'Pair-off amount',
        },
      },
    },
    bookkeeping: {
      noBookkeeping: 'No bookkept invoices, payments, settlements or credit usages found',
      fields: {
        id: 'ID',
        time: 'Date',
        creationTime: 'Creation time',
        modificationTime: 'Modification time',
        ignored: 'Ignored',
        status: 'Status',
        terminalStatus: 'Terminal status',
        backofficeStatus: 'Backoffice status',
        ticket: 'Ticket',
        amount: 'Amount',
        merchantId: 'Merchant ID',
        error: 'Error',
        other: 'Other',
        invoiceType: 'Invoice type',
        invoiceNumber: 'Invoice number',
        remoteInvoiceNumber: 'Remote invoice number',
        paymentType: 'Payment type',
        toPayer: 'To Payer',
        creditInvoiceNumber: 'Credit invoice number',
        creditUsageId: 'Credit usage ID',
        aggregatedPayoutJobId: 'Aggregated payout job ID',
        noInvoiceType: 'No invoice type found',
        noInvoiceNumber: 'No invoice number found',
        noPaymentType: 'No payment type found',
        noToPayer: 'No status found',
        noCreditInvoiceNumber: 'No credit invoice number found',
        noCreditUsageId: 'No credit usage ID found',
        noAggregatedPayoutJobId: 'No aggregated payout job ID found',
      },
      listView: {
        header: 'Bookkeeping',
        filters: {
          causedError: {
            title: 'Caused an error',
          },
          isTerminalStatusReached: {
            title: 'Terminal status reached',
            true: 'Yes',
            false: 'No',
          },
          isIgnored: {
            title: 'Ignored',
            true: 'Yes',
            false: 'No',
          },
        },
      },
      actions: {
        ignore: {
          openModal: 'Ignore',
          title: 'Ignore bookkept entity',
          body: 'Mark the bookkept entity <1/> meaning we we will not try to sync it again? Please also provide some information about why.',
          backofficeTicket: 'Backoffice ticket',
          submit: 'Yes',
        },
        retrySync: {
          openModal: 'Retry sync',
          title: 'Retry sync soon',
          body: 'Retry syncing the bookkept entity on the next scheduled round?',
          doUpdateBackoffice: 'Also update backoffice status and ticket?',
          backofficeTicket: 'Backoffice ticket',
          submit: 'Yes',
        },
      },
    },
    invoices: {
      invoices: 'Invoices',
      noInvoices: 'No invoices found',
      applyFilter: 'Apply a filter to load invoices',
      sentReminder: 'Sent reminder',
      reminders: 'Reminders',
      automaticRemindersEnabled: 'Automatic reminders are enabled',
      automaticRemindersDisabled: 'Automatic reminders are disabled',
      numberRemindersSent_one: '{{count}} reminder sent',
      numberRemindersSent_other: '{{count}} reminders sent',
      sentForDebtCollection: 'Sent for debt collection',
      debtCollection: 'Debt collection',
      debtCollectionEnabled: 'Debt collection is enabled',
      debtCollectionNotEnabled: 'Debt collection is not enabled',
      debtCollectionCancelled: 'Debt collection has been cancelled for this invoice',
      hideEmptyValues: 'Hide empty values',
      mouseHovers: {
        gln: "Optional. If not specified, the Registration Number will be used to lookup a GLN to send to. If there are multiple GLNs for that Registration Number, invoice creation will fail. In that case you'll need to specify this field to be able to successfully create the invoice.",
      },
      fields: {
        amountStart: '',
        amountEnd: '',
        company: 'Company',
        companyName: 'Company name',
        partOfInvoiceAggregation: 'Part of invoice statement',
        customerNumber: 'Customer number',
        deliveryType: 'Delivery type',
        dueDate: 'Due date',
        eInvoice: 'E-Invoice',
        externalInvoiceNumber: 'External invoice number',
        invoiceDate: 'Invoice date',
        invoiceNumber: 'Invoice number',
        customer: 'Customer',
        lineItem: {
          lineItem: 'Line item',
          fields: {
            description: 'Description',
            quantity: 'Quantity',
            subtotalPrice: 'Subtotal price (inc. VAT)',
            subtotalPriceExcludingVat: 'Subtotal price (ex. VAT)',
            subtotalVatAmount: 'Subtotal VAT amount',
            unitPrice: 'Unit price (inc. VAT)',
            unitVatAmount: 'Unit VAT amount',
            unitPriceExcludingVat: 'Unit price (Ex. VAT)',
            vatPercentage: 'VAT percentage',
            vatAccountCode: 'VAT account code',
            unit: 'Unit',
            articleNumber: 'Article number',
            salesAccountCode: 'Sales account code',
            costCenter: 'Cost center',
            additionalDetail: 'Additional detail',
            ean: 'EAN',
            uri: 'URI',
          },
        },
        link: 'Link',
        webview: 'Web view',
        orderApprovalDate: 'Order approval date',
        orderReference: 'Order Reference',
        registrationNumber: 'Registration number',
        personalNumber: 'Personal number',
        status: 'Status',
        site: 'Site',
        vatId: 'VAT ID',
        paymentStatus: 'Payment status',
        reference: 'Reference',
      },
      listView: {
        noSummary: 'No invoices to summarize',
        total: 'Total',
        noInvoicesFound: 'Found no invoices matching your filters',
      },
      actions: {
        approveOrder: 'Approve order',
        creditFeeBalance: 'Credit Fee Balance',
        sendForDebtCollection: 'Send for debt collection',
        stopDebtCollection: 'Stop automatic debt collection',
        openWebviewInNewTab: 'Open web view in new tab',
        openInvoiceInNewTab: 'Open invoice in new tab',
        editDraftInvoice: 'Edit draft invoice',
        downloadPdf: 'Download invoice PDF',
        downloadPdfForInvoice: 'Download PDF for invoice {{invoiceNumber}}',
        finalizeDraftInvoice: 'Finalize',
        badDebt: 'Mark invoice as bad debt',
        manualPayment: {
          title: 'Add manual payment',
          modalTitle: 'Add manual payment for invoice {{invoiceNumber}}',
          amountFieldLabel: 'Payment amount',
          success: 'Manual payment added for invoice {{invoiceNumber}}',
        },
        manualCreditUsage: {
          title: 'Add manual credit usage',
          modalTitle: 'Add manual credit usage for invoice {{invoiceNumber}}',
          description:
            'You can add both positive and negative manual credit usages for the invoice. A positive amount will reduce the available credit balance, while a negative amount will increase the available credit balance.',
          amountFieldLabel: 'Credit usage amount',
          success: 'Manual credit usage added for credit invoice {{invoiceNumber}}',
        },
        creditInvoiceFully: {
          openModal: 'Credit invoice fully',
          title: 'Credit invoice {{invoiceNumber}} fully',
          titleWithExternalInvoiceNumber: 'Credit invoice {{invoiceNumber}} ({{externalInvoiceNumber}}) fully',
          invoiceDate: 'Invoice date',
          submit: 'Credit invoice',
          success: 'Invoice {{invoiceNumber}} credited fully',
        },
        updateInvoice: {
          openModal: 'Update invoice',
          title: 'Update Invoice {{invoiceNumber}}',
          invoiceDate: 'Invoice date',
          submit: 'Update invoice',
        },
        creditInvoiceManually: {
          openModal: 'Credit invoice manually',
          title: 'Credit invoice {{invoiceNumber}} manually',
          titleWithExternalInvoiceNumber: 'Credit invoice {{invoiceNumber}} ({{externalInvoiceNumber}}) manually',
          invoiceDate: 'Invoice date',
          standalone: 'Standalone',
          standaloneDescription:
            'If checked this credit invoice will be a standalone credit invoice with no link to the original invoice.',
          sourceInvoiceIsDraftDisclaimer: 'From a draft invoice you can only create a standalone credit invoice.',
          submit: 'Credit invoice',
          success: 'Manual credit created for invoice {{invoiceNumber}}',
        },
        pairOff: {
          openModal: 'Pair off credit invoice',
          title: 'Pair off credit invoice {{creditInvoiceNumber}}',
          search: 'Search for invoice number',
          amountTitle: 'Amount to credit',
          submit: 'Pay invoice with credit invoice',
          creditInvoiceCardTitle: 'Credit invoice {{invoiceNumber}}',
          invoiceCardTitle: 'Invoice {{invoiceNumber}}',
          customerNameMatches: 'Customer name matches credit invoice customer',
          customerRegNumberMatches: 'Customer registration number matches credit invoice customer',
          customerNameMismatch: 'Customer name does not match credit invoice customer!',
          customerRegNumberMismatch: 'Customer registration number does not match credit invoice customer!',
          success:
            'Credited invoice {{toPayInvoiceNumber}} with amount {{amount}} from credit invoice {{creditInvoiceNumber}}',
        },
      },
      CreateInvoicePage: {
        openPage: 'Create invoice',
        title: 'New invoice',
        clear: 'Clear',
        submit: 'Send invoice',
        saveDraft: 'Save draft',
        infoRowModal: 'Add info row',
        itemRowModal: 'Add invoice item',
        addRow: 'Add row',
        addItemRow: 'Add item row',
        addInfoRow: 'Add info row',
        invoiceStatementInfo:
          'By checking this box, the invoice will be included in the monthly invoice statement. This means that the invoice date and due date specified will be ignored and the invoice will inherit invoice date and due date from the monthly invoice statement instead.',
        options: 'Options',
        draft: 'Draft',
        ourReference: 'Our reference',
        contractReference: 'Contract reference',
        roundOff: 'Round off',
        personalTitle: 'Title',
        state: 'State',
        coAddress: 'C/O address',
        contactInformation: 'Contact information',
        customerAndDeliveryHeader: 'Customer & delivery',
        customerAddress: 'Customer address',
        customerEmailAddress: 'Invoice email',
        emailCC: 'Email CC',
        emailBCC: 'Email BCC',
        houseAddress: 'House address',
        itemType: 'Item type',
        item: 'Item',
        freeform: 'Freeform',
        findCustomer: 'Find customer',
        findCustomerSearchPlaceholder: 'Search for customer',
        invoiceCustomers: 'Invoice customers',
        findCustomerCopyCustomer: 'Copy customer',
        findCustomerCopyCustomerAndInvoice: 'Copy customer & invoice',
        findCustomerEnterQuery: 'Enter a query to search for customers',
        findCustomerNoResultsForQuery: 'Found no customer matching the query "{{query}}" and the applied filters',
        findCustomerSortOrderInfo: 'Results are sorted by most recently created invoice customer',
        infoLine: 'Info line',
        clickToMoveItemUp: 'Click to move item up',
        clickToMoveItemDown: 'Click to move item down',
        dragAndDropToReorder: 'Drag and drop to reorder items',
        note: 'Note',
        disableReminders: 'Disable reminders',
        disableDebtCollection: 'Disable debt collection',
        externalPaymentReference: 'External payment reference',
        additionalCustomerInformation: 'Additional customer information',
        additionalInvoiceDetails: 'Additional invoice details',
        additionalItemDetails: 'Additional item details',
        totalAmountOverrides: 'Total amount overrides',
        invoicePdfPreview: 'Invoice PDF',
        invoicePdfPreviewTip:
          'The preview is generated from the invoice data you have entered and the current system state. It may not reflect the final invoice in all aspects.',
      },
      CopyInvoiceCustomerModal: {
        matchingCustomers: '{{count}} matching customers',
      },
    },
    dashboard: {
      title: 'Dashboard',
      widgets: {
        overdueInvoices: {
          title: 'Overdue invoices',
          description: 'Unpaid invoices overdue for more than 5 days',
          viewInvoices: 'View overdue invoices',
          countLabel: 'Invoices',
          empty: 'No overdue invoices',
          error: 'Failed to load overdue invoices',
          amounts: {
            totalAmountToPay: 'Amount to pay',
            totalBalance: 'Balance',
            feeBalance: 'Fee balance',
          },
        },
        settledPayments: {
          title: 'Settled payments in {{monthName}}',
          viewPayments: 'View settled payments',
          countLabel: 'Payments',
          totalAmount: 'Total amount',
          empty: 'No settled payments',
          error: 'Failed to load settled payments',
        },
        unmappedBankFilePayments: {
          title: 'Unmapped bank file payments',
          viewPayments: 'View unmapped payments',
          countLabel: 'Payments requiring mapping',
          totalAmount: 'Total amount',
          empty: 'No unmapped bank file payments',
          error: 'Failed to load unmapped bank file payments',
        },
      },
    },
    invoiceAggregations: {
      actions: {
        markAsManuallyPaid: {
          title: 'Mark statement as manually paid',
          externalReference: 'External payment reference',
          bookingDate: 'Booking date',
          success: 'Statement marked as manually paid',
          fillRequired: 'Please specify a booking date',
          info: 'This action marks the statement and all of its constituent invoices as paid. A payment will be created for each constituent invoice, using the provided external payment reference suffixed with the invoice running number. Additionally, any reminder fees on the constituent invoices will be cleared.',
        },
      },
      fields: {
        ocrNumber: 'OCR number',
      },
      listView: {
        title: 'Invoice statements',
        emptyComponent: 'No invoice statements',
        noInvoiceStatementsFound: 'Found no invoice statements matching your filters',
        table: {
          headers: {
            col1: 'Statement reference',
            col2: 'Recipient customer',
            col3: 'Number of invoices',
            col4: 'Due date',
            col5: 'Original amount',
            col6: 'Balance',
            col7: 'Currency',
            col8: 'Link',
            col9: 'Payment status',
            col10: 'Reminder(s) sent',
            col11: 'Actions',
          },
        },
      },
      singleView: {
        title: 'Invoice statement',
        noPayments: 'No payments',
        noInvoices: 'No invoices',
        details: {
          title: 'Details',
          invoiceAggregationNumber: 'Invoice statement number',
          invoiceCustomerId: 'Receiving customer number',
          invoiceCustomerCompany: 'Receiving customer name',
          originalAmountToPay: 'Original amount to pay',
          currencyCode: 'Currency',
          publicInvoiceUrl: 'Public invoice URL',
          deliveryType: 'Delivery type',
          deliveryTime: 'Delivery time',
          dueDate: 'Due date',
          periodStart: 'Start of invoice statement period',
          periodEnd: 'End of invoice statement period',
          createdDate: 'Invoice statement created',
          balance: 'Balance',
          countReminders: 'Number of reminders sent',
          countInvoices: 'Number of invoices',
        },
        creditUsage: {
          title: 'Credit usage',
          empty: 'No credit usage',
          table: {
            headers: {
              col1: 'Payment id',
              col2: 'Mapping date',
              col3: 'Credit used',
              col4: 'Invoice number',
            },
          },
        },
      },
    },
    onboard: {
      registration: {
        registrations: 'Registrations',
        fields: {
          id: 'ID',
          merchantId: 'Merchant',
          company: 'Company',
          status: 'Status',
          creditResult: 'Credit result',
          contactPerson: 'Ordering customer',
          date: 'Date',
        },
        listView: {
          empty: 'No registrations found',
        },
        singleView: {
          actionNeeded: 'Action needed',
        },
        actions: {
          approve: 'Approve',
          reject: 'Reject',
          creditVerification: {
            approveButton: 'Approve credit',
            approveDescription: 'Do you want to approve a credit limit?',
            descriptionAddLimit: 'Credit limit',
            descriptionWithLimit: 'Do you want to approve a credit limit {{amount}} {{currencyCode}}?',
            rejectButton: 'Reject credit',
            rejectDescription:
              'Do you want to reject the credit? Rejecting the credit will still allow you to add the customer, but with a registered credit limit of {{amount}} {{currencyCode}}.',
          },
          identityVerification: {
            approveButton: 'Approve identity verification',
            approveDescription: 'Do you want to approve and complete Identity verification?',
            rejectButton: 'Reject identity verification',
            rejectDescription:
              'Do you want to reject Identity verification? Identity verification and registration status will be changed to FAILED',
          },
        },
      },
      customer: {
        customers: 'Customers',
        fields: {
          companyName: 'Company name',
          country: 'Country',
          merchantId: 'Merchant',
          regNumber: 'Registration number',
          status: 'Status',
          creditLimit: 'Credit limit',
        },
        listView: {
          empty: 'No customers found',
          pendingSessions_one: 'Customer has 1 pending session',
          pendingSessions_other: 'Customer has {{count}} pending sessions',
          failedSessions_one: 'Customer has 1 recent failed session',
          failedSessions_other: 'Customer has {{count}} recent failed sessions',
          manualControlSessions_one: 'Customer has 1 session that requires manual control',
          manualControlSessions_other: 'Customer has {{count}} sessions that require manual control',
          errorSessions_one: 'Customer has 1 error session',
          errorSessions_other: 'Customer has {{count}} error sessions',
        },
        singleView: {
          summary: 'Summary',
          people: {
            people: 'People',
            name: 'Name',
            email: 'Email',
            phone: 'Phone number',
            role: 'Role',
            empty: 'No people found for this customer',
            deletePersonMessage: 'Do you want to delete "{{name}}"?',
            deletePersonButton: 'DELETE',
          },
          credit: {
            amount: 'Amount',
            date: 'Date',
            description: 'Description',
            creditor: 'Creditor',
          },
        },
      },
    },
    paymentrequests: {
      CreatePaymentRequestPage: {
        openPage: 'Create payment request',
        title: 'Create a payment request',
        subtitle: 'Payment request',
        description:
          'Create a payment request that you can send to your customer to accept payments. Customers who open the link will get redirected to a Payer hosted payment page and you will be able to see the transaction in the Payer portal.',
        fields: {
          externalPaymentReference: 'External payment reference',
          externalPaymentReferencePlaceholder: 'Add your payment or order reference',
        },
      },
      PaymentRequestPage: {
        title: 'Payment request',
        generatedLink: {
          header: 'Generated payment request',
          description: 'Copy and share the link below with your customer in the desired channel, like email or chat.',
          copyButtonTooltip: 'Copy link',
        },
        paymentLink: 'Payment link',
        details: 'Details',
        sendSms: {
          description: 'Share the payment link to your customer via SMS',
          sendButton: 'Send SMS',
          label: 'Phone number (E.164 format, e.g. +46701234567)',
          sent: 'Payment request has been sent to your customer via SMS',
        },
      },
    },
    payments: {
      listView: {
        noPaymentsFound: 'Found no payments matching your filters',
      },
      payment: 'Payment',
      payments: 'Payments',
      fields: {
        orderId: 'Order ID',
        additionalReference: 'Additional reference',
        createdAt: 'Created at',
        description: 'Description',
        paymentDate: 'Payment date',
        effectiveBookingDate: 'Effective booking date',
        externalPaymentReference: 'External payment reference',
        advancedOptions: 'Advanced options',
        bookingDate: 'Booking date',
        initialAmount: 'Initial amount',
        mappingDate: 'Mapping date',
        merchantDescription: 'Description',
        merchantId: 'Merchant',
        paymentId: 'Payment ID',
        remoteId: 'Remote ID',
        errorStatus: 'Error status',
        reason: 'Reason',
        paymentWithId: 'Payment {{id}}',
        time: 'Time',
        type: 'Payment type',
        updatedAt: 'Updated at',
        bookingDates: 'Booking dates',
        bookingDatesLink: 'View in payment details',
        customBookingDate: 'Custom booking date',
      },
      singleView: {
        card: {
          header: 'Card details',
          cardBrand: 'Card brand',
        },
        history: 'History',
        noPaymentHistory: 'No payment history found',
        noSettlements: 'No settlements found',
        openBanking: {
          header: 'Open banking details',
          openBankingReference: 'Open banking reference',
          bankName: 'Bank name',
          iban: 'Iban',
          accountNumber: 'Account number',
          accountOwner: 'Account holder',
        },
        order: {
          header: 'Order details',
          orderReference: 'Order reference',
          invoice: 'Invoice number',
          invoiceWebView: 'Invoice web view',
        },
        settlements: 'Settlements',
      },
      actions: {
        export: {
          exportResultsButton: 'Export Results',
          exportResultToPyramid: 'Pyramid - Incoming Payments Import Routine',
          exportResultToPyramidTooltipInputs: 'You must select a date interval in the filters',
        },
        captureCardPayment: {
          openModal: 'Capture card payment',
          title: 'Capture card payment {{paymentId}}',
          fullCapture: 'Full capture',
          releaseRemainingFunds: 'Release remaining funds',
          amountTitle: 'Amount to capture',
          submit: 'Capture',
          transactionDescription: 'Transaction description',
          success: 'Card payment {{paymentId}} captured!',
        },
        refundCardPayment: {
          openModal: 'Refund card payment',
          title: 'Refund card payment {{paymentId}}',
          fullRefund: 'Full refund',
          amountTitle: 'Amount to refund',
          submit: 'Refund',
          transactionDescription: 'Transaction description (optional)',
          success: 'Card payment {{paymentId}} refund initiated!',
        },
        refundCloudPosPayment: {
          openModal: 'Refund POS payment',
          title: 'Refund POS payment {{paymentId}}',
          fullRefund: 'Full refund',
          amountTitle: 'Amount to refund',
          submit: 'Refund',
          transactionDescription: 'Transaction description (optional)',
          success: 'POS payment {{paymentId}} refund initiated!',
        },
        releaseCardPayment: {
          openModal: 'Release Authorization',
          title: 'Release Authorization payment {{paymentId}}',
          body: 'Are you sure you want to release the authorization for card payment {{paymentId}}?',
          submit: 'Release',
          success: 'Authorization released for card payment {{paymentId}}!',
        },
        refundSwishPayment: {
          openModal: 'Refund swish payment',
          title: 'Refund swish payment {{paymentId}}',
          fullRefund: 'Full refund',
          amountTitle: 'Amount to refund',
          customExternalReferenceId: 'Custom reference',
          externalReferenceId: 'Your reference',
          customMessage: 'Custom message',
          message: 'Message to display in Swish app',
          defaultMessage: '',
          submit: 'Refund',
          success: 'Swish payment {{paymentId}} refunded!',
        },
      },
    },
    paymentTerminals: {
      noTerminals: 'No payment terminals found',
      listView: {
        title: 'Payment terminals',
        columns: {
          terminalName: 'Terminal name',
          registeredAt: 'Registered at',
        },
      },
    },
    settlements: {
      settlement: 'Settlement',
      payoutDate: 'Payout date',
      bookingDate: 'Booking date',
      toMerchant: 'To merchant',
      valueDate: 'Value date',
      payoutReference: 'Payout reference',
      marketCountryCode: 'Market',
      transactionMessage: 'Transaction message',
      schemeTotalFeeExcludingVat: 'Scheme fee excl. VAT',
      interchangeTotalFeeExcludingVat: 'Interchange fee excl. VAT',
      payerTotalFeeExcludingVat: 'Payer fee excl. VAT',
      feeVatPercentage: 'Total fees VAT percentage',
      totalFeesExcludingVat: 'Total fees excl. VAT',
      totalFeesVatAmount: 'Total fees VAT',
      fees: 'Fee details',
      listView: {
        header: 'Settlements',
        noneFound: 'No settlements found',
        exportCsv: 'Export to CSV',
        downloadCsv: 'Download settlement CSV',
        downloadHumanFriendlyCsv: 'Download settlement CSV (human friendly version)',
        openPdf: 'Open PDF',
        tooManySearchResultsForCsvExport:
          'Too many search results for CSV export, max {{maxSettlementsPerCsv}} allowed',
      },
      singleView: {
        payments: 'Payments',
        noPayments: 'No payments found',
        payment: {
          commonHeader: 'Payment',
          id: 'ID',
          paymentType: 'Type',
          additionalReference: 'Additional reference',
          externalPaymentReference: 'External payment reference',
          orderReferenceId: 'Order reference ID',
          invoiceNumber: 'Invoice number',
          initialAmount: 'Initial amount',
        },
        operation: {
          commonHeader: 'Operation',
          type: 'Type',
          amount: 'Amount',
          createdDate: 'Time',
          description: 'Description',
        },
      },
    },
    subscriptions: {
      externalReferenceId: 'External reference',
      numberOfBillings: 'Number of billings',
      nextBillingDate: 'Next billing date',
      endDate: 'End date',
      listView: {
        header: 'Subscriptions',
        noneFound: 'No subscriptions found',
      },
      actions: {
        cancel: {
          openModal: 'Cancel subscription',
          title: 'Cancel subscription',
          body: 'Cancel subscription {{externalReferenceId}}?',
          submit: 'Cancel subscription',
        },
        update: 'Update subscription',
      },
      CreateSubscriptionPage: {
        openPage: 'Create subscription',
        clear: 'Clear',
        submit: 'Send subscription',
        cardSubscriptionModal: {
          title: 'Copy card widget url',
          message:
            "The card subscription was successfully created. Don't forget to copy the card widget url and send it to your customer. The URL is: {{cardWidgetURL}}.",
        },
      },
      UpdateSubscriptionPage: {
        openPage: 'Update subscription',
        clear: 'Clear changes',
        submit: 'Update subscription',
        unsavedChanges: 'The subscription has been updated but not saved. Are you sure you want to leave?',
      },
    },

    enums: {
      clientNotificationEventType: {
        name: 'Notification event',
        values: {
          INVOICE_NEARING_DEBT_COLLECTION: 'Invoice nearing debt collection',
          CARD_PAYMENT_SETTLED: 'Card payment settled',
          CARD_PAYMENT_ERROR: 'Card payment error',
        },
      },
      clientNotificationChannel: {
        name: 'Notification channel',
        values: {
          EMAIL: 'Email',
          IN_APP: 'Merchant portal',
        },
      },
      clientNotificationScopeType: {
        name: 'Notification scope',
        values: {
          GENERAL: 'All invoices',
          SITE: 'Specific sites',
        },
      },
      backofficeStatus: {
        name: 'Backoffice status',
        values: {
          null: 'None',
          INVESTIGATION_STARTED: 'Investigation started',
          INVESTIGATION_BLOCKED: 'Investigation blocked',
          INVESTIGATION_COMPLETED: 'Investigation completed',
        },
      },
      bankAccountHolderType: {
        name: 'Bank account holder type',
        values: {
          COMPANY: 'Company',
          PERSON: 'Person',
          MERCHANT: 'Merchant',
          PAYER: 'Payer',
        },
      },
      countryCode: {
        name: 'Country',
        noCountriesConfigured: 'No countries configured',
        selectCountry: 'Select country',
        values: {
          [CountryCode.AT]: 'Austria',
          [CountryCode.AU]: 'Australia',
          [CountryCode.AX]: 'Åland Islands',
          [CountryCode.BE]: 'Belgium',
          [CountryCode.BG]: 'Bulgaria',
          [CountryCode.BR]: 'Brazil',
          [CountryCode.CA]: 'Canada',
          [CountryCode.CI]: 'Ivory Coast',
          [CountryCode.CH]: 'Switzerland',
          [CountryCode.CL]: 'Chile',
          [CountryCode.CN]: 'China',
          [CountryCode.CY]: 'Cyprus',
          [CountryCode.CZ]: 'Czechia',
          [CountryCode.DE]: 'Germany',
          [CountryCode.DK]: 'Denmark',
          [CountryCode.EE]: 'Estonia',
          [CountryCode.ES]: 'Spain',
          [CountryCode.FI]: 'Finland',
          [CountryCode.FR]: 'France',
          [CountryCode.GB]: 'United Kingdom',
          [CountryCode.GR]: 'Greece',
          [CountryCode.HK]: 'Hong Kong',
          [CountryCode.HR]: 'Croatia',
          [CountryCode.HU]: 'Hungary',
          [CountryCode.IE]: 'Ireland',
          [CountryCode.IL]: 'Israel',
          [CountryCode.IN]: 'India',
          [CountryCode.IT]: 'Italy',
          [CountryCode.IS]: 'Iceland',
          [CountryCode.JP]: 'Japan',
          [CountryCode.LT]: 'Lithuania',
          [CountryCode.LU]: 'Luxembourg',
          [CountryCode.LV]: 'Latvia',
          [CountryCode.MT]: 'Malta',
          [CountryCode.NL]: 'Netherlands',
          [CountryCode.NO]: 'Norway',
          [CountryCode.NZ]: 'New Zealand',
          [CountryCode.PL]: 'Poland',
          [CountryCode.PT]: 'Portugal',
          [CountryCode.RO]: 'Romania',
          [CountryCode.RS]: 'Serbia',
          [CountryCode.SE]: 'Sweden',
          [CountryCode.SI]: 'Slovenia',
          [CountryCode.SK]: 'Slovakia',
          [CountryCode.SG]: 'Singapore',
          [CountryCode.TR]: 'Turkey',
          [CountryCode.TH]: 'Thailand',
          [CountryCode.US]: 'United States of America',
          [CountryCode.AL]: 'Albania',
          [CountryCode.DZ]: 'Algeria',
          [CountryCode.AS]: 'American Samoa',
          [CountryCode.AD]: 'Andorra',
          [CountryCode.AO]: 'Angola',
          [CountryCode.AI]: 'Anguilla',
          [CountryCode.AQ]: 'Antarctica',
          [CountryCode.AG]: 'Antigua and Barbuda',
          [CountryCode.AR]: 'Argentina',
          [CountryCode.AM]: 'Armenia',
          [CountryCode.AW]: 'Aruba',
          [CountryCode.AZ]: 'Azerbaijan',
          [CountryCode.BS]: 'Bahamas (the)',
          [CountryCode.BH]: 'Bahrain',
          [CountryCode.BD]: 'Bangladesh',
          [CountryCode.BB]: 'Barbados',
          [CountryCode.BZ]: 'Belize',
          [CountryCode.BJ]: 'Benin',
          [CountryCode.BM]: 'Bermuda',
          [CountryCode.BT]: 'Bhutan',
          [CountryCode.BO]: 'Bolivia (Plurinational State of)',
          [CountryCode.BQ]: 'Bonaire, Sint Eustatius and Saba',
          [CountryCode.BA]: 'Bosnia and Herzegovina',
          [CountryCode.BW]: 'Botswana',
          [CountryCode.BV]: 'Bouvet Island',
          [CountryCode.IO]: 'British Indian Ocean Territory (the)',
          [CountryCode.BN]: 'Brunei Darussalam',
          [CountryCode.BF]: 'Burkina Faso',
          [CountryCode.BI]: 'Burundi',
          [CountryCode.CV]: 'Cabo Verde',
          [CountryCode.KH]: 'Cambodia',
          [CountryCode.CM]: 'Cameroon',
          [CountryCode.KY]: 'Cayman Islands (the)',
          [CountryCode.CF]: 'Central African Republic (the)',
          [CountryCode.TD]: 'Chad',
          [CountryCode.CX]: 'Christmas Island',
          [CountryCode.CC]: 'Cocos (Keeling) Islands (the)',
          [CountryCode.CO]: 'Colombia',
          [CountryCode.KM]: 'Comoros (the)',
          [CountryCode.CD]: 'Congo (the Democratic Republic of the)',
          [CountryCode.CG]: 'Congo (the)',
          [CountryCode.CK]: 'Cook Islands (the)',
          [CountryCode.CR]: 'Costa Rica',
          [CountryCode.CW]: 'Curaçao',
          [CountryCode.DJ]: 'Djibouti',
          [CountryCode.DM]: 'Dominica',
          [CountryCode.DO]: 'Dominican Republic (the)',
          [CountryCode.EC]: 'Ecuador',
          [CountryCode.EG]: 'Egypt',
          [CountryCode.SV]: 'El Salvador',
          [CountryCode.GQ]: 'Equatorial Guinea',
          [CountryCode.ER]: 'Eritrea',
          [CountryCode.SZ]: 'Eswatini',
          [CountryCode.ET]: 'Ethiopia',
          [CountryCode.FK]: 'Falkland Islands (the) [Malvinas]',
          [CountryCode.FO]: 'Faroe Islands (the)',
          [CountryCode.FJ]: 'Fiji',
          [CountryCode.GF]: 'French Guiana',
          [CountryCode.PF]: 'French Polynesia',
          [CountryCode.TF]: 'French Southern Territories (the)',
          [CountryCode.GA]: 'Gabon',
          [CountryCode.GM]: 'Gambia (the)',
          [CountryCode.GE]: 'Georgia',
          [CountryCode.GH]: 'Ghana',
          [CountryCode.GI]: 'Gibraltar',
          [CountryCode.GL]: 'Greenland',
          [CountryCode.GD]: 'Grenada',
          [CountryCode.GP]: 'Guadeloupe',
          [CountryCode.GU]: 'Guam',
          [CountryCode.GT]: 'Guatemala',
          [CountryCode.GG]: 'Guernsey',
          [CountryCode.GN]: 'Guinea',
          [CountryCode.GW]: 'Guinea-Bissau',
          [CountryCode.GY]: 'Guyana',
          [CountryCode.HT]: 'Haiti',
          [CountryCode.HM]: 'Heard Island and McDonald Islands',
          [CountryCode.VA]: 'Holy See (the)',
          [CountryCode.HN]: 'Honduras',
          [CountryCode.ID]: 'Indonesia',
          [CountryCode.IQ]: 'Iraq',
          [CountryCode.IM]: 'Isle of Man',
          [CountryCode.JM]: 'Jamaica',
          [CountryCode.JE]: 'Jersey',
          [CountryCode.JO]: 'Jordan',
          [CountryCode.KZ]: 'Kazakhstan',
          [CountryCode.KE]: 'Kenya',
          [CountryCode.KI]: 'Kiribati',
          [CountryCode.KR]: 'Korea (the Republic of)',
          [CountryCode.KW]: 'Kuwait',
          [CountryCode.KG]: 'Kyrgyzstan',
          [CountryCode.LA]: "Lao People's Democratic Republic (the)",
          [CountryCode.LB]: 'Lebanon',
          [CountryCode.LS]: 'Lesotho',
          [CountryCode.LR]: 'Liberia',
          [CountryCode.LY]: 'Libya',
          [CountryCode.LI]: 'Liechtenstein',
          [CountryCode.MO]: 'Macao',
          [CountryCode.MG]: 'Madagascar',
          [CountryCode.MW]: 'Malawi',
          [CountryCode.MY]: 'Malaysia',
          [CountryCode.MV]: 'Maldives',
          [CountryCode.ML]: 'Mali',
          [CountryCode.MH]: 'Marshall Islands (the)',
          [CountryCode.MQ]: 'Martinique',
          [CountryCode.MR]: 'Mauritania',
          [CountryCode.MU]: 'Mauritius',
          [CountryCode.YT]: 'Mayotte',
          [CountryCode.MX]: 'Mexico',
          [CountryCode.FM]: 'Micronesia (Federated States of)',
          [CountryCode.MD]: 'Moldova (the Republic of)',
          [CountryCode.MC]: 'Monaco',
          [CountryCode.MN]: 'Mongolia',
          [CountryCode.ME]: 'Montenegro',
          [CountryCode.MS]: 'Montserrat',
          [CountryCode.MA]: 'Morocco',
          [CountryCode.MZ]: 'Mozambique',
          [CountryCode.NA]: 'Namibia',
          [CountryCode.NR]: 'Nauru',
          [CountryCode.NP]: 'Nepal',
          [CountryCode.NC]: 'New Caledonia',
          [CountryCode.NI]: 'Nicaragua',
          [CountryCode.NE]: 'Niger (the)',
          [CountryCode.NG]: 'Nigeria',
          [CountryCode.NU]: 'Niue',
          [CountryCode.NF]: 'Norfolk Island',
          [CountryCode.MP]: 'Northern Mariana Islands (the)',
          [CountryCode.OM]: 'Oman',
          [CountryCode.PK]: 'Pakistan',
          [CountryCode.PW]: 'Palau',
          [CountryCode.PA]: 'Panama',
          [CountryCode.PG]: 'Papua New Guinea',
          [CountryCode.PY]: 'Paraguay',
          [CountryCode.PE]: 'Peru',
          [CountryCode.PH]: 'Philippines (the)',
          [CountryCode.PN]: 'Pitcairn',
          [CountryCode.PR]: 'Puerto Rico',
          [CountryCode.QA]: 'Qatar',
          [CountryCode.MK]: 'Republic of North Macedonia',
          [CountryCode.RW]: 'Rwanda',
          [CountryCode.RE]: 'Réunion',
          [CountryCode.BL]: 'Saint Barthélemy',
          [CountryCode.SH]: 'Saint Helena, Ascension and Tristan da Cunha',
          [CountryCode.KN]: 'Saint Kitts and Nevis',
          [CountryCode.LC]: 'Saint Lucia',
          [CountryCode.MF]: 'Saint Martin (French part)',
          [CountryCode.PM]: 'Saint Pierre and Miquelon',
          [CountryCode.VC]: 'Saint Vincent and the Grenadines',
          [CountryCode.WS]: 'Samoa',
          [CountryCode.SM]: 'San Marino',
          [CountryCode.ST]: 'Sao Tome and Principe',
          [CountryCode.SA]: 'Saudi Arabia',
          [CountryCode.SN]: 'Senegal',
          [CountryCode.SC]: 'Seychelles',
          [CountryCode.SL]: 'Sierra Leone',
          [CountryCode.SX]: 'Sint Maarten (Dutch part)',
          [CountryCode.SB]: 'Solomon Islands',
          [CountryCode.SO]: 'Somalia',
          [CountryCode.ZA]: 'South Africa',
          [CountryCode.GS]: 'South Georgia and the South Sandwich Islands',
          [CountryCode.SS]: 'South Sudan',
          [CountryCode.LK]: 'Sri Lanka',
          [CountryCode.SD]: 'Sudan (the)',
          [CountryCode.SR]: 'Suriname',
          [CountryCode.SJ]: 'Svalbard and Jan Mayen',
          [CountryCode.TW]: 'Taiwan (Province of China)',
          [CountryCode.TJ]: 'Tajikistan',
          [CountryCode.TZ]: 'Tanzania, United Republic of',
          [CountryCode.TL]: 'Timor-Leste',
          [CountryCode.TG]: 'Togo',
          [CountryCode.TK]: 'Tokelau',
          [CountryCode.TO]: 'Tonga',
          [CountryCode.TT]: 'Trinidad and Tobago',
          [CountryCode.TN]: 'Tunisia',
          [CountryCode.TM]: 'Turkmenistan',
          [CountryCode.TC]: 'Turks and Caicos Islands (the)',
          [CountryCode.TV]: 'Tuvalu',
          [CountryCode.UG]: 'Uganda',
          [CountryCode.UA]: 'Ukraine',
          [CountryCode.AE]: 'United Arab Emirates (the)',
          [CountryCode.UM]: 'United States Minor Outlying Islands (the)',
          [CountryCode.UY]: 'Uruguay',
          [CountryCode.UZ]: 'Uzbekistan',
          [CountryCode.VU]: 'Vanuatu',
          [CountryCode.VN]: 'Viet Nam',
          [CountryCode.VG]: 'Virgin Islands (British)',
          [CountryCode.VI]: 'Virgin Islands (U.S.)',
          [CountryCode.WF]: 'Wallis and Futuna',
          [CountryCode.EH]: 'Western Sahara',
          [CountryCode.YE]: 'Yemen',
          [CountryCode.ZM]: 'Zambia',
          [CountryCode.ZW]: 'Zimbabwe',
        },
      },
      customerType: {
        values: {
          [CustomerType.ORGANISATION]: 'Company',
          [CustomerType.PRIVATE]: 'Consumer',
          [CustomerType.MUNICIPAL]: 'Municipality/state',
        },
      },
      languageCode: {
        noLanguagesConfigured: 'No languages configured',
        selectLanguage: 'Select language',
        name: 'Language',
        values: {
          [LanguageCode.da]: 'Danish',
          [LanguageCode.de]: 'German',
          [LanguageCode.en]: 'English',
          [LanguageCode.es]: 'Spanish',
          [LanguageCode.fi]: 'Finnish',
          [LanguageCode.fr]: 'French',
          [LanguageCode.nl]: 'Dutch',
          [LanguageCode.sv]: 'Swedish',
        },
      },
      bookkeepingStatus: {
        name: 'Bookkeeping status',
        values: {
          NEW: 'New',
          BOOKKEPT: 'Bookkept',
          IGNORED: 'Ignored',
          CUSTOMER_CREATED: 'Customer created',
          CREATED: 'Created',
          ALREADY_CREATED: 'Already created',
          CREDITED: 'Credited',
          FROM_REVERSE_SYNC: 'From reverse sync',
          ERROR: 'Error',
          WAITING_FOR_SETTLEMENT: 'Waiting for settlement',
          WAITING_FOR_SETTLEMENT_RECHECK_NEEDED: 'Waiting for settlement, recheck needed',
          HANDLED_AS_CREDIT_USAGE: 'Handled as credit usage',
        },
      },
      BookkeptEntityCausedErrorFilter: {
        name: 'Bookkept entity caused error',
        values: {
          NEVER: 'Never',
          PREVIOUSLY: 'Previously',
          CURRENTLY: 'Currently',
        },
      },
      deliveryType: {
        name: 'Delivery type',
        values: {
          NONE: 'None',
          EMAIL: 'Email',
          PRINT: 'Print',
          SMS: 'SMS',
          EINVOICE: 'Einvoice',
        },
      },
      doNotMapReason: {
        name: 'Do not map reason',
        values: {
          null: 'None',
          IGNORED: 'Ignored',
          REFUNDED: 'Refunded',
          SPLITTED: 'Splitted',
          CARD_SETTLEMENT: 'Card settlement',
          OVERPAYMENT: 'Overpayment',
          FEE: 'Fee',
        },
      },
      mappingError: {
        name: 'Mapping error',
        values: {
          null: 'None',
          OVERPAYMENT: 'Overpayment',
          DENIED_ORDER: 'Denied order',
        },
      },
      mappingErrorEmailSentConcerningDescription: {
        name: 'Mapping error email sent description',
        values: {
          null: 'None',
          OVERPAYMENT: 'Mail regarding attempted over payment, sent to buyer',
          DENIED_ORDER: 'Mail regarding attempted payment to invoice for denied order, sent to merchant',
        },
      },
      fundSourceType: {
        name: 'Fund source',
        values: {
          PAYMENT_TO_ACCOUNT: 'Payment to account',
          CREDIT_INVOICE: 'Credit invoice',
        },
      },
      paymentAndCreditUsageStatus: {
        name: 'Payment / credit usage status',
        values: {
          [PaymentStatus.PAID]: 'Paid',
          [PaymentStatus.PARTIALLY_PAID]: 'Partially paid',
          [PaymentStatus.UNPAID]: 'Unpaid',
          [PaymentStatus.USED]: 'Credit used',
          [PaymentStatus.RESERVED]: 'Reserved',
          [PaymentStatus.PARTIALLY_USED]: 'Credit partially used',
          [PaymentStatus.UNUSED]: 'Credit unused',
          OVERDUE: 'Overdue',
        },
      },
      orderStatus: {
        name: 'Order status',
        values: {
          NEW: 'New',
          APPROVED: 'Approved',
          DENIED: 'Denied',
        },
      },
      invoiceType: {
        name: 'Type',
        values: {
          NORMAL: 'Normal',
          PREPAYMENT: 'Prepayment',
          ESCROW: 'Escrow',
          CASH: 'Cash',
          CREDIT: 'Credit',
        },
      },
      invoiceStatus: {
        name: 'Status',
        values: {
          NEW: 'New',
          DRAFT: 'Draft',
          FINAL: 'Final',
          CANCELLED: 'Cancelled',
        },
      },
      currencyCode: {
        name: 'Currency',
      },
      invoiceAggregationStatus: {
        name: 'Status',
        values: {
          [InvoiceAggregationStatus.FROZEN]: 'Frozen',
          [InvoiceAggregationStatus.SENDING_FAILED]: 'Failed to send',
          [InvoiceAggregationStatus.SUCCESSFULLY_SENT]: 'Successfully sent',
        },
      },
      paymentMethod: {
        name: 'Payment method',
        values: {
          BANK: 'Bank',
          CARD: 'Card',
          KLARNA: 'Klarna',
          SWISH: 'Swish',
          CREDIT_INVOICE: 'Credit invoice',
          MANUAL: 'Manual',
          WRITE_OFF: 'Write off',
          DIRECT_BANK: 'Open banking',
          POS: 'CloudPOS',
          REBALANCED: 'Rebalanced',
          BAD_DEBT_WRITE_OFF: 'Bad debt write off',
          EXPECTED_BAD_DEBT_WRITE_OFF: 'Expected bad debt write off',
        },
      },
      paymentStatus: {
        name: 'Status',
        values: {
          INITIATED: 'Initiated',
          AUTHORIZED: 'Authorized',
          PARTIALLY_CAPTURED: 'Partially captured',
          FULLY_CAPTURED: 'Fully captured',
          FULLY_REFUNDED: 'Fully refunded',
          REFUND_INITIATED: 'Refund in progress',
          REFUND_FAILED: 'Refund failed',
          CANCELLED: 'Failed',
          AWAITING_ADDITIONAL_SIGNATURE: 'Awaiting additional signature',
        },
      },
      paymentOperationType: {
        name: 'Type',
        values: {
          PARTIALLY_ACCEPTED: 'Partially accepted',
          AUTH: 'Auth',
          RELEASE: 'Release',
          REFUND: 'Refund',
          REFUND_INITIATED: 'Refund initiated',
          REFUND_FAILED: 'Refund failed',
          CAPTURE: 'Capture',
          DECLINED: 'Declined',
          CANCELLED: 'Cancelled',
          ERROR: 'Error',
          AUTHORIZE: 'Auth',
          INIT: 'Init',
        },
      },
      saltedgeSessionStatus: {
        name: 'Session status',
        values: {
          PENDING_BANK_SELECTION: 'Initiated',
          PENDING_ACCOUNT_SELECTION: 'Pending account selection',
          PENDING_PAYMENT_APPROVAL: 'Pending payment',
          PENDING_SUCCESSFUL_CALLBACK: 'Pending callback',
          PENDING_PARTIALLY_ACCEPTED: 'Pending multi-sign',
          SUCCESSFUL: 'Successful',
          FAILED: 'Failed',
          CANCELLED: 'Cancelled',
        },
      },
      sessionStatus: {
        name: 'Session status',
        values: {
          CREATED: 'Created',
          STARTED: 'Started',
          PENDING: 'Pending',
          COMPLETED: 'Completed',
          MANUAL_CONTROL: 'Manual control',
          FAILED: 'Failed',
          ERROR: 'Error',
        },
      },
      creditResult: {
        name: 'Credit result',
        values: {
          null: 'None',
          APPROVED: 'Approved',
          REJECTED: 'Rejected',
          CONTROL: 'Control',
        },
      },
      customerOrganisationMemberDecisionType: {
        name: 'Decision type',
        values: {
          AUTO: 'Automatic',
          MANUAL: 'Manual',
        },
      },
      customerOrganisationMemberStatus: {
        name: 'Decision',
        values: {
          APPROVED: 'Approved',
          REJECTED: 'Rejected',
          PENDING: 'Pending',
        },
      },
      identityVerificationType: {
        BANK_ID_VERIFICATION: 'BankID',
      },
      sessionActionType: {
        BANK_ID_VERIFICATION: 'Identity verification',
        APPROVER_VERIFICATION: 'Approver verification',
        EMAIL_VERIFICATION: 'Email verification',
        APPROVER_SIGN: 'Approver signing',
        CREDIT_CHECK: 'Credit check',
        COMPLIANCE_VERIFICATION: 'Compliance verification',
      },
      sessionActionReason: {
        name: 'Reason',
        values: {
          UNKNOWN: '-',
          NOT_YET_PERFORMED: 'Verification has not been performed yet',
          COULD_NOT_PERFORM_CHECK: 'Could not perform verification',
          COUNTRY_NOT_SUPPORTED: 'Verification not performed due to customer not registered in a supported country',
          MANUAL_DECISION: 'Status based on manual decision',
          PREVIOUS_DECISION: 'Status based on decision for a previous registration',
          PREVIOUS_MANUAL_DECISION: 'Status based on manual decision for a previous registration',
          NO_MATCH_FOUND: 'No matching person found',
          INEXACT_MATCH: 'Did not find an exact match',
          MATCHING_SIGNER: 'Person match registered board member or authorized signatory',
          SUCCESSFUL_AUTHENTICATION: 'Authentication was completed successfully',
          SUCCESSFUL_AND_MATCHING_AUTHENTICATION: 'Person matches authentication data',
          THIRD_PARTY_RESULT: '3rd party verification returned the status',
          EMAIL_VERIFIED: 'Ordering customer email address has been successfully verified',
          CREDIT_RESULT: 'Credit result according to credit rules',
        },
      },
      organisationMemberRoles: {
        SIGNER: 'Authorized signatory',
        BOARD_MEMBER: 'Board member',
        UNKNOWN: 'Ordering customer',
      },
      subscriptionPaymentMethod: {
        name: 'Payment method',
        values: {
          CARD: 'Card',
          INVOICE: 'Invoice',
        },
      },
      subscriptionPeriodTimeAndUnit: {
        name: 'Period',
        values: {
          DAY_one: '{{count}} day',
          DAY_other: '{{count}} days',
          WEEK_one: '{{count}} week',
          WEEK_other: '{{count}} weeks',
          MONTH_one: '{{count}} month',
          MONTH_other: '{{count}} months',
          YEAR_one: '{{count}} year',
          YEAR_other: '{{count}} years',
        },
      },
      subscriptionStatus: {
        name: 'Status',
        values: {
          ACTIVE: 'Active',
          PENDING: 'Pending',
          CANCELLED: 'Cancelled',
        },
      },
      creditRating: {
        name: 'Credit rating',
        values: {
          RATING_1_VERY_LOW_RISK: 'Rating 1, very low risk',
          RATING_2_LOW_RISK: 'Rating 2, low risk',
          RATING_3_MODERATE_RISK: 'Rating 3, moderate risk',
          RATING_4_NO_HISTORY: 'Rating 4, no history',
          RATING_5_HIGH_RISK: 'Rating 5, high risk',
          RATING_6_VERY_HIGH_RISK: 'Rating 6, very high risk',
          UNDETERMINED: 'Undetermined',
        },
      },
      creditDecisionReasonType: {
        values: {
          APPROVAL: 'Approval reasons',
          REJECTION: 'Rejection reasons',
          REQUIRES_CONTROL: 'Control reasons',
          IGNORED: 'Ignored reasons',
          NOT_CONSIDERED: 'Not considered reasons',
        },
      },
      creditDecisionReason: {
        name: 'Reasons',
        values: {
          CREDIT_RATING: 'Credit rating',
          IS_NEW: 'Is new',
          IS_OTHER_STATE_OR_GOVERNMENT_OR_MUNICIPALITY: 'Is state, government or municipality',
          IS_OTHER_FOUNDATIONS_OR_FUNDS: 'Is other foundations or funds',
          IS_OTHER_NON_PROFIT_ASSOCIATIONS: 'Is non-profit associations',
          NO_REASON_TO_APPROVE: 'No reason to approve',
          COMPANY_NOT_ACTIVE: 'Company not active',
          RECENT_BOARD_CHANGES: 'Recent board changes',
          NO_CORPORATE_TAX_REGISTRATION: 'No corporate tax registration',
          BANKRUPTCY: 'Bankruptcy',
          LIQUIDATION: 'Liquidation',
          OUTSTANDING_DEBT_AT_ENFORCEMENT_AGENCY_EXCEEDS_X: 'Outstanding debt at enforcement agency exceeds limit',
          PAYMENT_REMARKS_EXCEEDS_X: 'Payment remark exceeds limit',
          PETITIONS_AT_ENFORCEMENT_AGENCY_EXCEEDS_X: 'Petitions at enforcement agency exceeds limit',
          MANUAL_DECISION: 'Manual decision',
          INTERNATIONAL_COMPANY: 'International company',
          NO_CREDIT_DATA: 'No credit data',
        },
      },
      creditDecisionType: {
        name: 'Decision type',
        values: {
          MANUAL: 'Manual decision',
          AUTOMATIC: 'Automatic decision',
        },
      },
      legalForm: {
        name: 'Legal form',
        values: {
          LIMITED_COMPANY: 'Limited company',
          SOLE_PROPRIETORSHIP: 'Sole proprietorship',
          PARTNERSHIP: 'Partnership',
          OTHER_STATE_OR_GOVERNMENT_OR_MUNICIPALITY: 'State or government or municipality',
          OTHER_FOUNDATIONS_OR_FUNDS: 'Other foundations or funds',
          OTHER_NON_PROFIT_ASSOCIATIONS: 'Non-profit associations',
          OTHER: 'Other',
        },
      },
      periodTimeUnit: {
        name: 'Periodicity',
        values: {
          DAY: 'Day',
          WEEK: 'Week',
          MONTH: 'Month',
          YEAR: 'Year',
        },
      },
      paymentTerminalStatus: {
        name: 'Status',
        values: {
          INACTIVE: 'Disabled',
          ACTIVE: 'Active',
        },
      },
      paymentTerminalConnection: {
        name: 'Connection',
        values: {
          OFFLINE: 'Offline',
          ONLINE: 'Online',
        },
      },
    },
  },
};
